/**
 * Eval System API Service
 */

import { getAuthHeaders } from './authConfig';

const API_BASE = '/api/evals';

/**
 * Make an authenticated fetch request with organization context.
 */
async function authFetch(url, options = {}) {
  const authHeaders = getAuthHeaders();
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...authHeaders,
      ...options.headers,
    },
  });
  return response;
}

/**
 * List all evaluations
 */
export async function listEvals() {
  const response = await authFetch(API_BASE);
  if (!response.ok) {
    throw new Error(`Failed to list evals: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get eval details
 */
export async function getEval(evalId) {
  const response = await authFetch(`${API_BASE}/${evalId}`);
  if (!response.ok) {
    throw new Error(`Failed to get eval: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create an eval from config
 */
export async function createEval(data) {
  const response = await authFetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to create eval: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update an existing eval
 */
export async function updateEval(evalId, data) {
  const response = await authFetch(`${API_BASE}/${evalId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to update eval: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete an eval
 */
export async function deleteEval(evalId) {
  const response = await authFetch(`${API_BASE}/${evalId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete eval: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Copy an eval and add optimization configuration
 */
export async function copyAsOptimizable(evalId, data) {
  const response = await authFetch(`${API_BASE}/${evalId}/copy-as-optimizable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to copy eval: ${errorText || response.statusText}`);
  }
  return response.json();
}

/**
 * Start an eval run
 */
export async function startEvalRun(evalId, options = {}) {
  const response = await authFetch(`${API_BASE}/${evalId}/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  });
  if (!response.ok) {
    throw new Error(`Failed to start eval run: ${response.statusText}`);
  }
  return response.json();
}

/**
 * List runs for an eval
 */
export async function listEvalRuns(evalId, { limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset });
  const response = await authFetch(`${API_BASE}/${evalId}/runs?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to list runs: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get run details with trials
 */
export async function getEvalRun(runId) {
  const response = await authFetch(`${API_BASE}/runs/${runId}`);
  if (!response.ok) {
    throw new Error(`Failed to get run: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get A/B comparison data for a run
 */
export async function getRunComparison(runId) {
  const response = await authFetch(`${API_BASE}/runs/${runId}/comparison`);
  if (!response.ok) {
    throw new Error(`Failed to get comparison: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Cancel a running eval
 */
export async function cancelEvalRun(runId) {
  const response = await authFetch(`${API_BASE}/runs/${runId}/cancel`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to cancel run: ${response.statusText}`);
  }
  return response.json();
}

/**
 * List all schedules
 */
export async function listSchedules() {
  const response = await authFetch(`${API_BASE}/schedules`);
  if (!response.ok) {
    throw new Error(`Failed to list schedules: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create a schedule
 */
export async function createSchedule(data) {
  const response = await authFetch(`${API_BASE}/schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to create schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Trigger a schedule manually
 */
export async function triggerSchedule(scheduleId) {
  const response = await authFetch(`${API_BASE}/schedules/${scheduleId}/trigger`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to trigger schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update schedule enabled status
 */
export async function updateSchedule(scheduleId, enabled) {
  const response = await authFetch(`${API_BASE}/schedules/${scheduleId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enabled),
  });
  if (!response.ok) {
    throw new Error(`Failed to update schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete a schedule
 */
export async function deleteSchedule(scheduleId) {
  const response = await authFetch(`${API_BASE}/schedules/${scheduleId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * List available agents
 */
export async function listAgents() {
  const response = await authFetch('/api/agents');
  if (!response.ok) {
    throw new Error(`Failed to list agents: ${response.statusText}`);
  }
  return response.json();
}

/**
 * List available judges
 */
export async function listJudges() {
  const response = await authFetch('/api/judges');
  if (!response.ok) {
    throw new Error(`Failed to list judges: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get agent nodes (for graph-based agents)
 * @param {string} agentPath - Path to the agent file
 * @returns {{ agent_type: 'simple' | 'graph', nodes: Array<{name: string, type: string}> }}
 */
export async function getAgentNodes(agentPath) {
  const response = await authFetch(`/api/agents/${encodeURIComponent(agentPath)}/nodes`);
  if (!response.ok) {
    throw new Error(`Failed to get agent nodes: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get agent parameters (template variables)
 * @param {string} agentPath - Path to the agent file
 * @returns {{ parameters: Array<{name: string, required: boolean, description?: string}> }}
 */
export async function getAgentParameters(agentPath) {
  const response = await authFetch(`/api/agents/${encodeURIComponent(agentPath)}/parameters`);
  if (!response.ok) {
    throw new Error(`Failed to get agent parameters: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get agent versions (for version comparison in evals)
 * @param {string} agentId - Agent name or ID
 * @returns {{ item_id: string, name: string, content_type: string, versions: Array<{version_number: number, created_at: string, commit_message?: string, is_draft: boolean, content_hash?: string}> }}
 */
export async function getAgentVersions(agentId) {
  const response = await authFetch(`/api/agents/${encodeURIComponent(agentId)}/versions`);
  if (!response.ok) {
    throw new Error(`Failed to get agent versions: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get judge versions (for version comparison in evals)
 * @param {string} judgeId - Judge name or ID
 * @returns {{ item_id: string, name: string, content_type: string, versions: Array<{version_number: number, created_at: string, commit_message?: string, is_draft: boolean, content_hash?: string}> }}
 */
export async function getJudgeVersions(judgeId) {
  const response = await authFetch(`/api/judges/${encodeURIComponent(judgeId)}/versions`);
  if (!response.ok) {
    throw new Error(`Failed to get judge versions: ${response.statusText}`);
  }
  return response.json();
}

/**
 * List available models
 * @returns {Array<{value: string, label: string, provider: string}>}
 */
export async function listModels() {
  const response = await authFetch('/api/models');
  if (!response.ok) {
    throw new Error(`Failed to list models: ${response.statusText}`);
  }
  return response.json();
}

// Default export with all functions
export const evalApi = {
  listEvals,
  getEval,
  createEval,
  updateEval,
  deleteEval,
  copyAsOptimizable,
  startEvalRun,
  listEvalRuns,
  getEvalRun,
  getRunComparison,
  cancelEvalRun,
  listSchedules,
  createSchedule,
  triggerSchedule,
  updateSchedule,
  deleteSchedule,
  listAgents,
  listJudges,
  getAgentNodes,
  getAgentParameters,
  getAgentVersions,
  getJudgeVersions,
  listModels,
};
